import { useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { useChemoFetch } from './useChemoFetch';
import { useChemoRecordsEdit } from './useChemoRecordsEdit';
import { useChemoUI } from './useChemoUI';
import { useChemoSearch } from './useChemoSearch';
import { chemoTranslations } from './chemoTranslations';
import { SEND_CUSTOMER_MESSAGE_ENDPOINT, HTTP_METHOD_POST, CHEMO_DEFAULTS, FIELD_NAMES } from './chemoConstants';
import { formatDateForInput } from './useChemoReportForm';

const execute = async (method, url, data) => {
    const config = { withCredentials: true };
    const upperMethod = method.toUpperCase();

    let response;
    if (upperMethod === 'GET') {
        response = await axios.get(url, config);
    } else if (upperMethod === 'POST') {
        response = await axios.post(url, data, config);
    } else if (upperMethod === 'PATCH') {
        response = await axios.patch(url, data, config);
    } else {
        response = await axios({ method: upperMethod, url, data, ...config });
    }

    return response.data;
};

export const useChemoRecords = () => {
    const fetch = useChemoFetch(execute);
    const ui = useChemoUI();
    const search = useChemoSearch();
    const reportOffsetRef = useRef(0);

    const edit = useChemoRecordsEdit({
        fetchedRecords: fetch.fetchedRecords,
        setFetchedRecords: fetch.setFetchedRecords,
        originalRecords: fetch.originalRecords,
        KLINE: fetch.KLINE,
        execute,
        fetchReports: fetch.fetchReports,
        setLoading: fetch.setLoading,
        setError: fetch.setError
    });

    useEffect(() => {
        edit.syncEditedRecords(fetch.fetchedRecords);
    }, [fetch.fetchedRecords, edit.syncEditedRecords]);

    const submitReport = useCallback(async (reportData) => {
        fetch.setLoading(true);
        fetch.setError(null);
        try {
            const result = await fetch.fetchChemoRecords(reportData);
            if (result.success) {
                await fetch.fetchReports();
            }
            return result;
        } catch (err) {
            fetch.setError(err.message || chemoTranslations.errors.submitError);
            return { success: false, error: err.message };
        } finally {
            fetch.setLoading(false);
        }
    }, [fetch]);

    const handleSubmit = useCallback(async (reportData) => {
        return await fetch.fetchChemoRecords(reportData);
    }, [fetch.fetchChemoRecords]);

    const handleSaveReport = useCallback(async (reportData) => {
        const result = await edit.saveChemoReport(reportData);

        if (result.data && (result.data.results || result.data.errors)) {
            ui.showResultDialog(result.data);
        } else {
            if (result.success) {
                ui.showSnackbar(chemoTranslations.success.reportSaved, 'success');
            }
        }

        if (result.approvalWarning) {
            ui.showSnackbar(result.approvalWarning, 'warning');
        }

        if (result.success) {
            ui.closeForm();
        }

        return result;
    }, [edit.saveChemoReport, ui.showResultDialog, ui.showSnackbar, ui.closeForm]);

    const handleTopRecordsChange = useCallback((newTop, hospital) => {
        fetch.handleTopChange(newTop);
        if (hospital) {
            fetch.fetchHospitalReport(hospital, newTop);
        }
    }, [fetch.handleTopChange, fetch.fetchHospitalReport]);

    const handleDateSearch = useCallback(async () => {
        if (!search.dateSearch.startDate || !search.dateSearch.endDate) {
            ui.showSnackbar(chemoTranslations.errors.dateRangeRequired, 'warning');
            return;
        }

        const result = await fetch.fetchReportsByDate(
            search.dateSearch.startDate,
            search.dateSearch.endDate
        );
        if (!result.success) {
            ui.showSnackbar(`${chemoTranslations.errors.errorPrefix}${result.error}`, 'error');
        }
    }, [search.dateSearch, fetch.fetchReportsByDate, ui.showSnackbar]);



    const loadReportAtOffset = useCallback(async (hospital, offset) => {
        fetch.resetRecords();
        edit.resetEditedRecords();

        if (!hospital) {
            ui.openNewReportForm();
            return;
        }

        try {
            const top = offset + 1;
            const queryParams = new URLSearchParams({ hospitalName: hospital, top: String(top) });
            const url = `${process.env.REACT_APP_BASEURL}/chemo/hospital-report?${queryParams}`;
            const response = await execute('GET', url);
            const reports = Array.isArray(response) ? response : [response];
            const targetReport = reports[offset];

            if (!targetReport) {
                ui.showSnackbar(chemoTranslations.errors.noMoreReports, 'warning');
                return;
            }

            const fromDate = formatDateForInput(targetReport[FIELD_NAMES.FROMDATE]);
            const toDate = formatDateForInput(targetReport[FIELD_NAMES.TODATE]);

            if (fromDate && toDate) {
                reportOffsetRef.current = offset;
                ui.openNewReportWithDates(fromDate, toDate);
                fetch.fetchChemoRecords({
                    hospital,
                    startDate: fromDate,
                    endDate: toDate,
                    treatmentType: CHEMO_DEFAULTS.TREATMENT_TYPE,
                    productCode: CHEMO_DEFAULTS.PRODUCT_CODE
                });
            } else {
                ui.openNewReportForm();
            }
        } catch {
            if (offset === 0) {
                ui.openNewReportForm();
            } else {
                ui.showSnackbar(chemoTranslations.errors.noMoreReports, 'warning');
            }
        }
    }, [ui.openNewReportForm, ui.openNewReportWithDates, ui.showSnackbar, fetch.resetRecords, fetch.fetchChemoRecords, edit.resetEditedRecords]);

    const openNewReportForm = useCallback(async (hospital) => {
        reportOffsetRef.current = 0;
        await loadReportAtOffset(hospital, 0);
    }, [loadReportAtOffset]);

    const loadPreviousReport = useCallback(async (hospital) => {
        await loadReportAtOffset(hospital, reportOffsetRef.current + 1);
    }, [loadReportAtOffset]);

    const handleSendMessage = useCallback(async (hospital) => {
        const message = ui.messageDialog.message?.trim();
        if (!message) return;

        ui.setMessageSending(true);
        try {
            await execute(
                HTTP_METHOD_POST,
                `${process.env.REACT_APP_BASEURL}${SEND_CUSTOMER_MESSAGE_ENDPOINT}`,
                { message, hospital }
            );
            ui.closeMessageDialog();
            ui.showSnackbar(chemoTranslations.success.messageSent, 'success');
        } catch (err) {
            ui.showSnackbar(chemoTranslations.errors.messageSendError, 'error');
        } finally {
            ui.setMessageSending(false);
        }
    }, [ui]);

    return {
        reports: fetch.reports,
        loading: fetch.loading,
        error: fetch.error,
        searchTerm: search.searchTerm,
        showForm: ui.showForm,
        currentReport: ui.currentReport,
        fetchedRecords: fetch.fetchedRecords,
        editedRecords: edit.editedRecords,
        isLoadingRecords: fetch.isLoadingRecords,
        topRecords: fetch.topRecords,
        fetchReports: fetch.fetchReports,
        submitReport,
        fetchChemoRecords: fetch.fetchChemoRecords,
        fetchHospitalReport: fetch.fetchHospitalReport,
        fetchReportsByDate: fetch.fetchReportsByDate,
        updateEditedRecords: edit.updateEditedRecords,
        handleCheckboxChange: edit.handleCheckboxChange,
        handleFieldChange: edit.handleFieldChange,
        formatDate: search.formatDate,
        saveChemoReport: edit.saveChemoReport,
        openNewReportForm,
        loadPreviousReport,
        openExistingReport: ui.openExistingReport,
        closeForm: ui.closeForm,
        filterReports: search.filterReports,
        handleSearchChange: search.handleSearchChange,
        handleTopChange: fetch.handleTopChange,
        filteredReports: search.filterReports(fetch.reports, search.searchTerm),

        snackbar: ui.snackbar,
        showSnackbar: ui.showSnackbar,
        closeSnackbar: ui.closeSnackbar,
        resultDialog: ui.resultDialog,
        showResultDialog: ui.showResultDialog,
        closeResultDialog: ui.closeResultDialog,
        dateSearch: search.dateSearch,
        updateDateSearch: search.updateDateSearch,

        handleSubmit,
        handleSaveReport,
        handleTopRecordsChange,
        handleDateSearch,

        messageDialog: ui.messageDialog,
        openMessageDialog: ui.openMessageDialog,
        closeMessageDialog: ui.closeMessageDialog,
        updateMessageText: ui.updateMessageText,
        handleSendMessage
    };
};
